/**
 * The sentences the result screen leads with.
 *
 * Kept apart from the components because this is where the contract is easiest to misreport: a
 * failure to start is not a failed call, an unanswered call is not a quality problem, and a call
 * whose counters never arrived is not a call that went well. Each of those gets its own sentence,
 * and the order below is the order of precedence.
 */

import * as da from '@/strings/da'
import type { ApiFailure } from './api-failure'
import type { CallFailure } from './sip'
import type { CallTestResult, Verdict } from './types'

export interface NarrativeInput {
	apiFailure: ApiFailure | null
	callFailure: CallFailure | null
	connected: boolean
	verdict: Verdict
	worseDirection: 'INBOUND' | 'OUTBOUND' | null
	platform: CallTestResult | null
	platformStillPending: boolean
	inboundPackets: number
	remoteAudibleMs: number
}

export interface Narrative {
	headline: string
	notes: string[]
}

export function buildNarrative(input: NarrativeInput): Narrative {
	return { headline: headlineFor(input), notes: notesFor(input) }
}

function headlineFor(input: NarrativeInput): string {
	// Never got as far as a call: the visitor's network is not the subject of this sentence.
	if (input.apiFailure) return da.apiFailure[input.apiFailure.kind]
	if (input.callFailure) return da.callFailure[input.callFailure]

	if (!input.connected) {
		const reason = input.platform?.call?.terminatedReason
		return reason ? da.notConnected(reason) : da.verdictHeadline.NO_AUDIO
	}

	return da.verdictHeadline[input.verdict]
}

function notesFor(input: NarrativeInput): string[] {
	const notes: string[] = []

	// Which way it went wrong, in the visitor's own terms.
	if (input.connected && input.worseDirection) {
		notes.push(input.verdict === 'NO_AUDIO'
			? da.oneWay[input.worseDirection]
			: da.direction[input.worseDirection])
	}

	const reason = input.platform?.quality?.reason
	if (reason) notes.push(da.qualityReasonNote(reason))

	// Packets arriving but nothing audible points at a codec or gateway, not at the network.
	if (input.inboundPackets > 0 && input.remoteAudibleMs === 0) notes.push(da.call.deliveredButSilent)

	notes.push(...platformCoverageNotes(input))

	if (input.platformStillPending) notes.push(da.pendingGaveUp)

	return notes
}

/**
 * A READY result with no `audio` has two very different causes, and saying which one it is
 * matters: an unanswered call is the whole answer, while an answered call whose counters never
 * arrived leaves the browser's own numbers as the only evidence.
 */
function platformCoverageNotes(input: NarrativeInput): string[] {
	const platform = input.platform
	if (!platform || platform.audio) return []

	if (platform.status === 'NO_CALL' || platform.call?.answered === false) {
		return [da.noPlatformAudio.notAnswered]
	}

	if (platform.call?.answered) return [da.noPlatformAudio.countersMissing]

	return []
}

